import React, { useLayoutEffect, useRef, useState } from "react";
import { OverlayScroll } from "components/common/OverlayScroll";

/// Shared geometry for the main-app list pages (Overview, Datasets,
/// Connections, Trash, Access). Every page's column starts at the same height,
/// so navigating between them doesn't shift the search bar.

/// Overview carries a hero and a create strip around its list, so its list gets
/// a shorter cap to keep the total column comparable.
/// Rows there are `--item-height` (2rem) tall.
export const OVERVIEW_LIST_ROWS = 8;

/// Pins the column top so the search bar (2.5rem) sits level with the sidebar's
/// Notifications row (4rem down, 2rem tall), whatever this page holds — so content only
/// ever grows downward.
export const ColumnTopSpacer: React.FC = () => (
  <div style={{ flexShrink: 0, height: "3.75rem" }} />
);

/// A list page's footer count: the parts above zero, joined.
export const ListCount: React.FC<{ parts: (React.ReactNode | false)[] }> = ({
  parts,
}) => (
  <span
    className="font-mono text-fg-muted"
    style={{
      fontSize: "var(--font-size-x-small)",
      letterSpacing: "0.04em",
      fontVariantNumeric: "tabular-nums",
    }}
  >
    {parts.filter(Boolean).map((part, i) => (
      <React.Fragment key={i}>
        {i > 0 && " · "}
        {part}
      </React.Fragment>
    ))}
  </span>
);

/// A list page's scroll box: content height up to `rows` rows — nine on every
/// page but Overview, so the columns land in one height band — gliding between
/// sizes. Its thumb takes no width, and stays hidden below the cap so none
/// flashes mid-glide.
export const ListBox: React.FC<
  Omit<React.ComponentProps<"div">, "ref"> & {
    rows?: number;
    ref?: React.RefObject<HTMLDivElement | null>;
  }
> = ({ rows = 9, ref, children, ...rest }) => {
  const ownBox = useRef<HTMLDivElement>(null);
  const box = ref ?? ownBox;
  const inner = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();
  const [scrolls, setScrolls] = useState(false);
  useLayoutEffect(() => {
    const content = inner.current!;
    const measure = () => {
      const cap = parseFloat(getComputedStyle(box.current!).maxHeight);
      setHeight(content.offsetHeight > cap ? cap : content.offsetHeight);
      setScrolls(content.offsetHeight > cap);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [box]);

  return (
    <OverlayScroll
      {...rest}
      scrollRef={box}
      frameClassName={scrolls ? "" : "[&>.scrollbar-thumb]:hidden"}
      className="overflow-x-hidden"
      style={{
        maxHeight: `calc(var(--item-height) * ${rows})`,
        height,
        transition: "height var(--duration-medium) var(--ease-move)",
      }}
    >
      <div ref={inner}>{children}</div>
    </OverlayScroll>
  );
};
